/**
 * Clinic portal consent API.
 * Pure functions for consent list and detail operations. No React or localStorage.
 */

import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

export interface ConsentListItem {
  id: number;
  patient_id: number;
  serial_number: number;
  consent_date: string | null;
  is_special?: boolean;
  treatment_term_from?: string | null;
  treatment_term_to?: string | null;
  hospital_name?: string | null;
  doctor_name: string | null;
  /**
   * R2 only: CSV string of massage parts (empty when none).
   */
  massage?: string | null;
  orthomorphia: string | null;
  orthomorphia_check: string;
  /** A2 (consents) / R2 (r_consents): 1=必要とする→一覧「必要」, 2=必要としない→「不要」 */
  walking_condition_id?: number | null;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface ConsentListData {
  patient: any;
  consents: ConsentListItem[];
  total_count?: number;
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export type ConsentListQueryParams = {
  page?: number;
  per_page?: number;
  sort_type?: string;
  sort_dir?: string;
};

function toParams(params: ConsentListQueryParams | undefined): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  if (!params) return result;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Get consent list for a patient
 */
export async function getConsentsList(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  params?: ConsentListQueryParams
): Promise<ApiResponse<ConsentListData>> {
  const response = await apiClient.get<ApiResponse<ConsentListData>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/list`),
    {
      params: toParams(params),
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

/**
 * Create consent
 */
export async function createConsent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  payload: unknown,
  serviceType: string
): Promise<ApiResponse<{ consent: { id: number } }>> {
  const response = await apiClient.post<ApiResponse<{ consent: { id: number } }>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}`),
    payload,
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

/**
 * Get consent by ID
 */
export async function getConsentById(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  consentId: string,
  serviceType: string
): Promise<ApiResponse<{ consent: { id: number } }>> {
  const response = await apiClient.get<ApiResponse<{ consent: { id: number } }>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}`),
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

/**
 * Update consent
 */
export async function updateConsent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  consentId: string,
  payload: unknown,
  serviceType: string
): Promise<ApiResponse<{ consent: { id: number } }>> {
  const response = await apiClient.put<ApiResponse<{ consent: { id: number } }>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}`),
    payload,
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

/**
 * Delete consent
 */
export async function deleteConsent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  consentId: string,
  serviceType: string
): Promise<ApiResponse<unknown>> {
  const response = await apiClient.delete<ApiResponse<unknown>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}`),
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

export interface AhakiCounterpart {
  id: number;
  is_ahaki_both_treatment: boolean;
  is_not_transport_cost: boolean;
  walking_condition_id: number;
}

export interface AhakiCounterpartInfo {
  has_counterpart: boolean;
  counterpart: AhakiCounterpart | null;
  counterparts?: AhakiCounterpart[];
}

export interface LockedTreatmentMonthsInfo {
  months: string[];
}

/**
 * Check if patient has a counterpart (opposite service type) consent overlapping the
 * given treatment period — used for あはき両方施術 auto-setting on the consent form.
 */
export async function getAhakiCounterpartCheck(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  treatmentTermFrom: string,
  treatmentTermTo: string
): Promise<ApiResponse<AhakiCounterpartInfo>> {
  const response = await apiClient.get<ApiResponse<AhakiCounterpartInfo>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/ahaki-check`),
    {
      params: {
        treatment_term_from: treatmentTermFrom,
        treatment_term_to: treatmentTermTo,
      },
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Check printed/confirmed rezept months within the consent treatment period.
 */
export async function getLockedTreatmentMonths(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  treatmentTermFrom: string,
  treatmentTermTo: string
): Promise<ApiResponse<LockedTreatmentMonthsInfo>> {
  const response = await apiClient.get<ApiResponse<LockedTreatmentMonthsInfo>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/locked-treatment-months`),
    {
      params: {
        treatment_term_from: treatmentTermFrom,
        treatment_term_to: treatmentTermTo,
      },
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Patch a single あはき flag on a counterpart consent (B-1 / C-1 auto-update).
 * The counterpartServiceType must be the OPPOSITE service type from the current form.
 */
export async function patchAhakiCounterpartFlag(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  consentId: number,
  counterpartServiceType: string,
  flagName: 'is_ahaki_both_treatment' | 'is_not_transport_cost',
  value: boolean
): Promise<ApiResponse<null>> {
  const response = await apiClient.patch<ApiResponse<null>>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}/ahaki-flag`),
    { flag: flagName, value, service_type: counterpartServiceType },
    { headers: { 'X-Service-Type': counterpartServiceType } }
  );
  return response.data;
}

/** @deprecated Use getConsentsList */
export const fetchConsentList = getConsentsList;
/** @deprecated Use getConsentById */
export const fetchConsentDetail = getConsentById;
